import { Users, Calendar, Music, TrendingUp, Ruler, AlertTriangle } from 'lucide-react';

import type { RecitalStats } from '~entities/recital-manager/model/recital';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';

interface RecitalStatsTabProps {
  recitalId: string;
  stats?: RecitalStats;
}

export const RecitalStatsTab = ({ stats }: RecitalStatsTabProps) => {
  if (!stats) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-lg text-muted-foreground'>Loading statistics...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Shows',
      value: stats.totalShows,
      description: 'Number of shows in this recital',
      icon: Calendar,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Total Performances',
      value: stats.totalPerformances,
      description: 'Individual dance routines across all shows',
      icon: Music,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Total Students',
      value: stats.totalStudents,
      description: 'Unique students participating',
      icon: Users,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Costume Sizings',
      value: stats.sizingSubmissions || 0,
      description: 'Submissions received',
      icon: Ruler,
      color: 'text-pink-500',
      bgColor: 'bg-pink-50',
    },
    {
      title: 'Unresolved Conflicts',
      value: stats.conflictsCount || 0,
      description: 'Scheduling conflicts to resolve',
      icon: AlertTriangle,
      color: 'text-red-500',
      bgColor: 'bg-red-50',
    },
    {
      title: 'Avg Students per Performance',
      value:
        stats.totalPerformances > 0
          ? (stats.totalStudents / stats.totalPerformances).toFixed(1)
          : '0',
      description: 'Average class size per performance',
      icon: TrendingUp,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <div className='space-y-6'>
      {/* Overview Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className='pb-2'>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-sm font-medium text-muted-foreground'>
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className='text-3xl font-bold'>{stat.value}</div>
                <p className='text-xs text-muted-foreground mt-1'>
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Additional Stats */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        <Card>
          <CardHeader>
            <CardTitle>Performance Distribution</CardTitle>
            <CardDescription>
              How performances are distributed across shows
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>Shows</span>
                <span className='font-semibold'>{stats.totalShows}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-medium text-muted-foreground'>Total Performances (All Shows)</span>
                <span className='font-bold text-lg'>{stats.totalPerformances}</span>
              </div>
              {stats.showBreakdown && stats.showBreakdown.length > 0 && (
                <div className='pt-2 border-t space-y-2'>
                  <span className='text-xs text-muted-foreground'>Per Show:</span>
                  {stats.showBreakdown.map((show) => (
                    <div key={show.showId} className='flex items-center justify-between pl-2'>
                      <span className='text-sm text-muted-foreground'>{show.showName}</span>
                      <span className='font-medium'>{show.performanceCount}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>
                  Avg per Show
                </span>
                <span className='font-semibold'>
                  {stats.totalShows > 0
                    ? (stats.totalPerformances / stats.totalShows).toFixed(1)
                    : '0'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Student Participation</CardTitle>
            <CardDescription>
              Overview of student involvement in the recital
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>
                  Total Students
                </span>
                <span className='font-semibold'>{stats.totalStudents}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>
                  Total Performances
                </span>
                <span className='font-semibold'>{stats.totalPerformances}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>
                  Avg per Student
                </span>
                <span className='font-semibold'>
                  {stats.totalStudents > 0
                    ? (stats.totalPerformances / stats.totalStudents).toFixed(1)
                    : '0'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Costume Sizing</CardTitle>
            <CardDescription>
              Parent submissions for costume sizing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>
                  Submissions
                </span>
                <span className='font-semibold'>{stats.sizingSubmissions || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>
                  Total Students
                </span>
                <span className='font-semibold'>{stats.totalStudents}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>
                  Completion Rate
                </span>
                <span className='font-semibold'>
                  {stats.totalStudents > 0
                    ? `${((stats.sizingSubmissions || 0) / stats.totalStudents * 100).toFixed(0)}%`
                    : '0%'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle>Recital Status</CardTitle>
          <CardDescription>Current state and readiness</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-center gap-3'>
            <div
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                stats.status === 'published'
                  ? 'bg-green-100 text-green-800'
                  : stats.status === 'draft'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
              }`}
            >
              {stats.status ? stats.status.toUpperCase() : 'UNKNOWN'}
            </div>
            <span className='text-sm text-muted-foreground'>
              {stats.status === 'published'
                ? 'Recital is live and visible to parents'
                : stats.status === 'draft'
                  ? 'Recital is in draft mode'
                  : 'Recital has been archived'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
